import type { MessageKey } from "@gioia365/i18n";
import type { QueryClient } from "@tanstack/react-query";
import type { ComponentType } from "react";
import type { IconName } from "../design/components/Icon";
import { prefetchActivity } from "../activity/activity-queries";
import { prefetchConversations } from "../conversations/conversation-queries";
import { WhatsAppRailCount } from "../conversations/WhatsAppUnread";
import { NewLeadsRailCount } from "../home/NewLeadsRailCount";
import { prefetchLeadList } from "../leads/lead-queries";
import type { AppRouter } from "../router";
import { prefetchOpportunities } from "../opportunities/opportunity-queries";
import { prefetchSalesReport } from "../reports/report-queries";
import { activityScopeOf, conversationScopeOf, leadScopeOf, opportunityScopeOf, reportsScopeOf, smartListScopeOf, taskScopeOf, type Access, type MenuSubject } from "../session/access";
import type { TabContext } from "../session/tab-context";
import { prefetchSmartLists } from "../smart-lists/smart-list-queries";
import { SmartListRail, SmartListRailNews } from "../smart-lists/SmartListRail";
import { prefetchTasks } from "../tasks/tasks-queries";
import { settingsSectionAt, settingsSectionsOf } from "./settings/sections";

// Le voci della rail (piano M1b D15): l'unico punto dove si registrano. M2 e le milestone successive
// aggiungono una riga qui, con la loro schermata, invece di toccare `AppShell`; l'ordine canonico di
// UX_SYSTEM (Home, Task, Lead, Opportunità, Conversazioni, Attività, Report, Smart List, Impostazioni) vale
// anche per le voci che arriveranno. Nella rail c'è soltanto ciò che esiste: nessuna voce spenta.
//
// Le voci si calcolano sul menu della scheda (`MenuSubject`): quello di chi guarda oppure, nella Vista Commerciale,
// quello del Commerciale scelto, con le stesse regole sui suoi permessi e senza le voci della piattaforma (decisione del
// product owner del 2 ottobre 2026). Nella vista una pagina fuori da questo menu non si apre (`pageInMenu`).

export type RailIcon = IconName;

/** Un percorso vero del router: una voce che non porta da nessuna parte non compila. */
export type RailPath = keyof AppRouter["routesByPath"];

export type RailItem = {
  id: string;
  to: RailPath;
  label: MessageKey;
  icon: RailIcon;
  /**
   * Se la voce è nel menu: `subject.access` è nullo sulle pagine della piattaforma senza azienda nella scheda; nella Vista
   * Commerciale è l'accesso del Commerciale scelto.
   */
  visible(subject: MenuSubject): boolean;
  /**
   * Legge in anticipo i dati della schermata, così il passaggio alla sezione li mostra subito (velocità: criterio di
   * accettazione). Il guscio la chiama per le voci visibili appena ha il contesto della scheda (non nel contesto di
   * piattaforma, dove ogni lettura resta nel registro dell'azienda), e al passaggio del puntatore o del focus sulla voce.
   * `tab` è la scheda della guardia, con la sua Vista Commerciale; `tabStorage` è lo `Storage` della scheda (per esempio
   * l'ultima Smart List aperta).
   */
  prefetch?(input: { queryClient: QueryClient; access: Access; tab: TabContext; tabStorage: Storage }): void;
  /**
   * Il sottomenu della voce (piano M3 Task 8): il guscio lo mostra sotto di lei quando la rail è aperta e fissa (da
   * 1280 px, o con «Allarga il menu»: riscontro del product owner del 3 ottobre 2026, anche nel contesto di piattaforma)
   * e dentro il pannello del menu sul telefono (nel contesto di piattaforma soltanto dalla sua voce o nella sua sezione:
   * D13); con la rail a icone mai, nemmeno al passaggio del puntatore o con il focus (decisione del 4 ottobre 2026). Fuori
   * dal contesto di piattaforma resta nella pagina anche chiuso, nascosto e inerte, così aprire la rail non aggiunge nulla
   * sopra le voci che seguono: `open` dice se è aperto, e un sottomenu che legge dati li legge soltanto da aperto.
   * `onNavigate` richiude il pannello quando si sceglie una delle sue voci.
   */
  flyout?: ComponentType<{ open: boolean; onNavigate(): void }>;
  /**
   * Un segno sull'icona della voce quando la rail è a icone (per le Smart List, le «novità»): il componente disegna il
   * segno, nascosto agli screen reader, e le sue parole nell'elemento `id`, che il guscio dà alla voce come descrizione;
   * senza niente da dire non disegna nulla. Con la rail aperta e fissa non c'è: lo dice già il sottomenu.
   */
  indicator?: ComponentType<{ id: string }>;
  /**
   * Un numero accanto alla voce, con la rail aperta e fissa, con la rail a icone (sull'icona) e nel pannello del telefono (piano M6,
   * R3, passo 12b; D24: i WhatsApp da leggere accanto a «Conversazioni»): il componente disegna il numero, nascosto agli screen
   * reader, e le sue parole nell'elemento `id`, che il guscio dà alla voce come descrizione; a zero non disegna nulla.
   */
  count?: ComponentType<{ id: string }>;
  /**
   * L'inizio dell'indirizzo delle pagine della sezione, quando è più largo di `to`: le pagine della piattaforma stanno tutte
   * sotto `/gioia365`. Decide soltanto `pageInMenu`.
   */
  section?: string;
};

export const RAIL_ITEMS: readonly RailItem[] = [
  { id: "home", to: "/", label: "shell.nav.home", icon: "home", visible: ({ access }) => access !== null },
  // «Le cose da fare» (piano M5, Task 18), fra Home e Lead nell'ordine confermato; soltanto a chi vede almeno i suoi Task (l'area
  // `tasks`, con la regola del server: nella «Guarda come…» quella della persona scelta).
  {
    id: "tasks",
    to: "/task",
    label: "shell.nav.tasks",
    icon: "task",
    visible: ({ access }) => access !== null && taskScopeOf(access, "view") !== "none",
    // «Oggi», la vista di partenza, nella Vista Commerciale della scheda se c'è.
    prefetch: ({ queryClient, tab }) => void prefetchTasks(queryClient, tab),
  },
  // Fra Task (M5) e Opportunità (M6), nell'ordine confermato; soltanto a chi vede almeno i propri Lead.
  {
    id: "leads",
    to: "/lead",
    label: "shell.nav.leads",
    icon: "lead",
    visible: ({ access }) => access !== null && leadScopeOf(access, "view") !== "none",
    // La vista di partenza, nella Vista Commerciale della scheda se c'è.
    prefetch: ({ queryClient, tab }) => void prefetchLeadList(queryClient, tab),
    // I Lead nuovi da contattare della persona, come «Nuovi Lead da contattare» della Home (piano M6 D24).
    count: NewLeadsRailCount,
  },
  // «Opportunità» (piano M6, R8, Task 25), dopo Lead nell'ordine confermato; soltanto a chi vede almeno le Opportunità che segue (l'area
  // `opportunities`, con la regola del server: nella «Guarda come…» quella della persona scelta).
  {
    id: "opportunities",
    to: "/opportunita",
    label: "shell.nav.opportunities",
    icon: "opportunity",
    visible: ({ access }) => access !== null && opportunityScopeOf(access, "view") !== "none",
    // Le aperte, la vista di partenza (per il Commerciale le sue, SEZ-1), nella «Guarda come…» della scheda se c'è.
    prefetch: ({ queryClient, access, tab }) => void prefetchOpportunities(queryClient, tab, access),
  },
  // «Conversazioni» (piano M4, Task 19), dopo Lead e Opportunità nell'ordine confermato; soltanto a
  // chi vede almeno le sue telefonate (l'area `conversations`, con la regola del server: nella «Guarda come…» quella della
  // persona scelta).
  {
    id: "conversations",
    to: "/conversazioni",
    label: "shell.nav.conversations",
    icon: "conversations",
    visible: ({ access }) => access !== null && conversationScopeOf(access) !== "none",
    // Gli ultimi 7 giorni, la vista di partenza, nella «Guarda come…» della scheda se c'è.
    prefetch: ({ queryClient, tab }) => void prefetchConversations(queryClient, tab),
    // I WhatsApp da leggere (piano M6, R3, passo 12b; D24).
    count: WhatsAppRailCount,
  },
  // «Attività» («Chi ha fatto cosa», SEZ-8; M7 parte 2), dopo Conversazioni nell'ordine confermato (UX-3); soltanto a chi vede almeno le
  // sue azioni (l'area `activity`, con la regola del server: nella «Guarda come…» quella della persona scelta).
  {
    id: "activity",
    to: "/attivita",
    label: "shell.nav.activity",
    icon: "clock",
    visible: ({ access }) => access !== null && activityScopeOf(access) !== "none",
    // «Oggi», tutte le azioni che si vedono, nella «Guarda come…» della scheda se c'è.
    prefetch: ({ queryClient, tab }) => void prefetchActivity(queryClient, tab),
  },
  // «Report» (piano M7, Task 3; D11), dopo Attività nell'ordine confermato; soltanto a chi vede
  // almeno i suoi report (l'area `reports`, con la regola del server: nella «Guarda come…» quella della persona scelta).
  {
    id: "reports",
    to: "/report",
    label: "shell.nav.reports",
    icon: "report",
    visible: ({ access }) => access !== null && reportsScopeOf(access) !== "none",
    // «Commerciali», «Mese», «Tutto il team» (per chi vede i suoi, i suoi), nella «Guarda come…» della scheda se c'è.
    prefetch: ({ queryClient, tab }) => void prefetchSalesReport(queryClient, tab),
  },
  // Dopo Report (M7), nell'ordine confermato; soltanto a chi vede almeno le sue liste. Sotto, le liste con i numeri.
  {
    id: "smartLists",
    to: "/smart-list",
    label: "shell.nav.smartLists",
    icon: "smartlist",
    visible: ({ access }) => access !== null && smartListScopeOf(access, "view") !== "none",
    // Il menu e, appena arriva, la lista che la voce aprirà (decisione P28).
    prefetch: ({ queryClient, tab, tabStorage }) => void prefetchSmartLists(queryClient, tab, tabStorage),
    flyout: SmartListRail,
    indicator: SmartListRailNews,
  },
  // Con almeno una sezione delle Impostazioni nel menu (`routes/settings/sections.ts`).
  { id: "settings", to: "/impostazioni", label: "shell.nav.settings", icon: "settings", visible: (subject) => settingsSectionsOf(subject).length > 0 },
  // Soltanto il Super admin di Gioia365, e mai nella Vista Commerciale.
  { id: "platform", to: "/gioia365/aziende", section: "/gioia365", label: "shell.nav.platform", icon: "building", visible: ({ platformAdmin }) => platformAdmin },
];

/** Le voci della rail nel menu di `subject`, nell'ordine della rail. */
export function railItemsOf(subject: MenuSubject): RailItem[] {
  return RAIL_ITEMS.filter((item) => item.visible(subject));
}

/**
 * Se la pagina all'indirizzo `pathname` è nel menu di `subject`: la sezione della rail che la contiene è nel menu e, nelle
 * Impostazioni, anche la sua sezione. Una pagina che non sta in nessuna sezione della rail (la pagina «non trovata») non
 * è di nessun menu e resta aperta.
 */
export function pageInMenu(pathname: string, subject: MenuSubject): boolean {
  const owner = RAIL_ITEMS.find((item) => {
    const start = item.section ?? item.to;
    return start === "/" ? pathname === "/" : pathname === start || pathname.startsWith(`${start}/`);
  });
  if (!owner) return true;
  if (!owner.visible(subject)) return false;
  const section = owner.id === "settings" ? settingsSectionAt(pathname) : undefined;
  return section === undefined || settingsSectionsOf(subject).includes(section);
}
